/* eslint-disable no-param-reassign */
/* eslint-disable react/jsx-props-no-spreading */
import { FC, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { PositionDto } from '../../../Service/DocumentT';
import { EditableFavoritePositionProperty } from '../../../Atoms/EditableProperties/EditableFavoritePositionProperty';
import { DocumentContext } from '../../../Service/Context/DocumentContextV2';
import { EditableAutocomplete } from '../../../Atoms/inputs/EditableAutocomplete';
import { HtmlPlaceholderFormatter } from '../../../Atoms/Formatter/HtmlPlaceholderFormatter';
import { useEventScope } from '../../../Service/hooks/useEventScope';
import { EditableDocumentPositionProperty } from '../../../Atoms/EditableProperties/EditableDocumentPositionProperty';
import { useEventData } from '../../../Service/Context/EventParticipationContextV2';
import { DocumentTemplateContext } from '../../../Service/Context/DocumentTemplateContext';

interface Props {
  withLabel?: boolean;
  position: PositionDto;
  onChange: (patch: Partial<PositionDto>) => void;
  fullWidth?: boolean;
}

const PositionAmountInput: FC<Props> = ({ ...rest }) => {
  const { isEventScope } = useEventScope();

  if (isEventScope) {
    return <EventPositionAmountInput {...rest} />;
  }
  return <TemplatePositionAmountInput {...rest} />;
};

const TemplatePositionAmountInput: FC<Props> = ({ onChange, position, withLabel = false, fullWidth = false }) => {
  const { bookitupDocument } = useContext(DocumentTemplateContext);
  const { pathname } = useLocation();
  const { documentType } = bookitupDocument ?? {};
  const isPartialInvoice = documentType === 'PARTIAL_INVOICE';

  const preChange = (patch: Partial<PositionDto>) => {
    if (patch.amount) {
      patch.amount = patch.amount.replace(/(<([^>]+)>)/gi, '');
    }
    if (patch.amount === '{location_distance}') {
      patch.unit = 'km';
    } else if (patch.amount === '{event_hours}') {
      patch.unit = 'Std.';
    }

    return patch;
  };

  if (pathname.includes('positions')) {
    return (
      <EditableFavoritePositionProperty
        patchField="amount"
        preChange={preChange}
        changeHandler={onChange}
        entity={position}
        settings
        suggestions={[
          { amount: '{event_hours}' },
          { amount: '{event_days}' },
          { amount: '{event_nights}' },
          { amount: '{location_distance}' },
          { amount: '{location_double_distance}' },
          { amount: '{location_quadruple_distance}' },
        ]}
        event={undefined}
        _document={bookitupDocument}
        eventLocation={undefined}
        editFormatter={EditableAutocomplete}
        labelFormatter={HtmlPlaceholderFormatter}
        disabled={isPartialInvoice}
        placeholder={withLabel ? 'Menge' : undefined}
        name={undefined}
        style={{ width: 50, overflow: 'hidden', maxHeight: 34 }}
      />
    );
  }

  return (
    <EditableDocumentPositionProperty
      settings
      patchField="amount"
      preChange={preChange}
      changeHandler={onChange}
      entity={position}
      suggestions={[
        { amount: '{event_hours}' },
        { amount: '{event_days}' },
        { amount: '{event_nights}' },
        { amount: '{location_distance}' },
        { amount: '{location_double_distance}' },
        { amount: '{location_quadruple_distance}' },
      ]}
      _document={bookitupDocument}
      editFormatter={EditableAutocomplete}
      labelFormatter={HtmlPlaceholderFormatter}
      disabled={isPartialInvoice}
      placeholder={withLabel ? 'Menge' : undefined}
      style={{ width: fullWidth ? undefined : 50, overflow: 'hidden', maxHeight: 34 }}
      name={undefined}
    />
  );
};

const EventPositionAmountInput: FC<Props> = ({ onChange, position, withLabel = false, fullWidth = false }) => {
  const { bookitupDocument } = useContext(DocumentContext);
  const { distance, event, location } = useEventData();
  const { documentType } = bookitupDocument ?? {};
  const isPartialInvoice = documentType === 'PARTIAL_INVOICE';

  const preChange = (patch: Partial<PositionDto>) => {
    if (patch.amount) {
      patch.amount = patch.amount.replace(/(<([^>]+)>)/gi, '');
    }
    if (patch.amount === '{location_distance}') {
      if (distance && distance.distanceInMeters) {
        // patch.amount = `${Math.round(distance.distanceInMeters / 1000)}`;
        patch.unit = 'km';
      }
    } else if (patch.amount === '{event_hours}') {
      if (event && event.dates[0].hours) {
        // patch.amount = `${event.dates[0].hours}`;
        patch.unit = 'Std.';
      }
    }

    return patch;
  };

  return (
    <EditableDocumentPositionProperty
      patchField="amount"
      preChange={preChange}
      changeHandler={onChange}
      entity={position}
      suggestions={[
        { amount: '{event_hours}' },
        { amount: '{event_days}' },
        { amount: '{event_nights}' },
        { amount: '{location_distance}' },
        { amount: '{location_double_distance}' },
        { amount: '{location_quadruple_distance}' },
      ]}
      event={event}
      _document={bookitupDocument}
      eventLocation={location}
      editFormatter={EditableAutocomplete}
      labelFormatter={HtmlPlaceholderFormatter}
      disabled={isPartialInvoice}
      placeholder={withLabel ? 'Menge' : undefined}
      style={{ width: fullWidth ? undefined : 50, overflow: 'hidden', maxHeight: 34 }}
      name={undefined}
    />
  );
};

export default PositionAmountInput;
